/**
 * OptiQueue Settings Page
 * 
 * Internal settings page for managing OptiQueue online booking configuration.
 * OptiQueue is Core (CORE-AR): every tier includes it. The page is available while the shop has OptiQueue switched
 * on (the OPTIQUEUE key in shops.addons is the shop's operational on/off preference, not a purchase).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route OptiQueue settings updated_at write through clock.
import { clock } from '@/utils/clock';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { supabase } from '@/integrations/supabase/client';
import { isOfferableCatalogJob, retiredCatalogSuffix } from '@/lib/catalog/newJobCatalogQuery';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Clock, Calendar, Users, Wrench, ExternalLink, Copy, Check, Layers, Phone, FileText, AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { TopBar } from '@/components/layout/TopBar';

interface OptiQueueSettingsData {
  shop_id: string;
  enabled: boolean;
  advance_notice_min: number;
  days_ahead: number;
  max_waiters_per_day: number;
  waiter_allowed: boolean;
  dropoff_allowed: boolean;
  conservative_buffer_pct: number;
  allowed_job_keys: string[];
  // Lane configuration
  lane_maintenance_enabled: boolean;
  lane_diagnostics_enabled: boolean;
  lane_requested_work_enabled: boolean;
  lane_callback_enabled: boolean;
  diagnostic_default_minutes: number;
}

interface JobCatalogItem {
  id: string;
  label: string;
  category_key: string;
  sub_menu: string;
  retired_at?: string | null;
  superseded_by_code?: string | null;
}

const DEFAULT_SETTINGS: Omit<OptiQueueSettingsData, 'shop_id'> = {
  enabled: false,
  advance_notice_min: 120,
  days_ahead: 14,
  max_waiters_per_day: 6,
  waiter_allowed: true,
  dropoff_allowed: true,
  conservative_buffer_pct: 25,
  allowed_job_keys: [],
  lane_maintenance_enabled: true,
  lane_diagnostics_enabled: true,
  lane_requested_work_enabled: true,
  lane_callback_enabled: true,
  diagnostic_default_minutes: 60,
};

export default function OptiQueueSettings() {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const optiQueueEnabled = useFeatureFlag('optiQueue');
  
  const [settings, setSettings] = useState<OptiQueueSettingsData | null>(null);
  const [jobsCatalog, setJobsCatalog] = useState<JobCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Redirect if feature not enabled.
  // V-F-1d: Pre-V-F-1d this redirect targeted `/settings`, which is NOT a
  // registered route in App.tsx — users without the OPTIQUEUE addon hit
  // a toast then landed on the catch-all <NotFound />. Now redirects to
  // `/shop-profile` (the de-facto settings hub). Toast preserved.
  useEffect(() => {
    if (!optiQueueEnabled) {
      // Not a plan refusal: OptiQueue is Core. The shop has it switched off (operational preference).
      toast.error('OptiQueue is switched off for this shop');
      navigate('/shop-profile');
    }
  }, [optiQueueEnabled, navigate]);

  // Load settings and jobs catalog
  useEffect(() => {
    if (!activeShop?.id) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load OptiQueue settings
        const { data: existingSettings, error: settingsError } = await supabase
          .from('optiqueue_settings')
          .select('*')
          .eq('shop_id', activeShop.id)
          .maybeSingle();

        if (settingsError) throw settingsError;

        if (existingSettings) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...existingSettings
          } as OptiQueueSettingsData);
        } else {
          // Create default settings
          setSettings({
            shop_id: activeShop.id,
            ...DEFAULT_SETTINGS,
          });
        }

        // Load jobs catalog for allowed jobs selection
        // Only show maintenance, inspection, and tires categories for Lane 1 bookable services
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs_catalog')
          // Config picker: retired rows load so an already-allowed one still shows (badged);
          // jobsByCategory below never offers a retired row that is not already allowed (W2).
          .select('id, label, category_key, sub_menu, retired_at, superseded_by_code')
          .in('category_key', ['maintenance', 'inspection', 'inspections', 'tires'])
          .order('category_key')
          .order('label');

        if (jobsError) throw jobsError;
        setJobsCatalog(jobs || []);

      } catch (error) {
        console.error('Error loading OptiQueue settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [activeShop?.id]);

  const handleSave = async () => {
    if (!settings || !activeShop?.id) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('optiqueue_settings')
        .upsert({
          ...settings,
          shop_id: activeShop.id,
          updated_at: clock.now().toISOString(),
        }, {
          onConflict: 'shop_id',
        });

      if (error) throw error;
      toast.success('OptiQueue settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleJobKey = (jobId: string) => {
    if (!settings) return;
    
    const currentKeys = settings.allowed_job_keys || [];
    const newKeys = currentKeys.includes(jobId)
      ? currentKeys.filter(k => k !== jobId)
      : [...currentKeys, jobId];
    
    setSettings({ ...settings, allowed_job_keys: newKeys });
  };

  const copyBookingLink = async () => {
    if (!activeShop?.slug) {
      toast.error('Shop slug not configured');
      return;
    }
    
    const bookingUrl = `${window.location.origin}/book/${activeShop.slug}`;
    await navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    toast.success('Booking link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const openBookingPage = () => {
    if (!activeShop?.slug) {
      toast.error('Shop slug not configured');
      return;
    }
    window.open(`/book/${activeShop.slug}`, '_blank');
  };

  // Group jobs by category for display
  const jobsByCategory = jobsCatalog
    .filter((job) => isOfferableCatalogJob(job) || (settings?.allowed_job_keys ?? []).includes(job.id))
    .reduce((acc, job) => {
    if (!acc[job.category_key]) {
      acc[job.category_key] = [];
    }
    acc[job.category_key].push(job);
    return acc;
  }, {} as Record<string, JobCatalogItem[]>);

  if (!optiQueueEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <TopBar />
        <div className="flex-1 container mx-auto py-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar />
      <div className="flex-1 container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">OptiQueue Settings</h1>
            <p className="text-muted-foreground">Configure online booking for your shop</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/shop-profile?tab=admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button variant="outline" size="sm" onClick={copyBookingLink}>
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy Link
            </Button>
            <Button variant="outline" size="sm" onClick={openBookingPage}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>

      {/* Main Settings */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Enable Toggle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Online Booking
            </CardTitle>
            <CardDescription>
              Enable or disable the public booking page
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enabled" className="text-base">Enable Online Booking</Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, customers can book appointments online
                </p>
              </div>
              <Switch
                id="enabled"
                checked={settings?.enabled ?? false}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, enabled: checked })
                }
              />
            </div>
            {settings?.enabled && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Booking URL:</p>
                <code className="text-xs text-primary">
                  {window.location.origin}/book/{activeShop?.slug || 'your-shop'}
                </code>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Booking Lanes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Booking Lanes
            </CardTitle>
            <CardDescription>
              Choose which booking options are available to customers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-green-600" />
                <div>
                  <Label htmlFor="lane-maintenance" className="text-sm">Maintenance & Quick Services</Label>
                  <p className="text-xs text-muted-foreground">Direct booking for known jobs</p>
                </div>
              </div>
              <Switch
                id="lane-maintenance"
                checked={settings?.lane_maintenance_enabled ?? true}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, lane_maintenance_enabled: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <div>
                  <Label htmlFor="lane-diagnostics" className="text-sm">Diagnostics</Label>
                  <p className="text-xs text-muted-foreground">Gateway service (recommended: always on)</p>
                </div>
              </div>
              <Switch
                id="lane-diagnostics"
                checked={settings?.lane_diagnostics_enabled ?? true}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, lane_diagnostics_enabled: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <div>
                  <Label htmlFor="lane-requested" className="text-sm">Previously Recommended Work</Label>
                  <p className="text-xs text-muted-foreground">Requires shop confirmation</p>
                </div>
              </div>
              <Switch
                id="lane-requested"
                checked={settings?.lane_requested_work_enabled ?? true}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, lane_requested_work_enabled: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-red-600" />
                <div>
                  <Label htmlFor="lane-callback" className="text-sm">Callback Requests</Label>
                  <p className="text-xs text-muted-foreground">Customer wants to be contacted</p>
                </div>
              </div>
              <Switch
                id="lane-callback"
                checked={settings?.lane_callback_enabled ?? true}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, lane_callback_enabled: checked })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Scheduling Window */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Scheduling Window
            </CardTitle>
            <CardDescription>
              How far in advance can customers book
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Days Ahead</Label>
                <span className="text-sm font-medium">{settings?.days_ahead ?? 14} days</span>
              </div>
              <Slider
                value={[settings?.days_ahead ?? 14]}
                onValueChange={([value]) => 
                  settings && setSettings({ ...settings, days_ahead: value })
                }
                min={1}
                max={30}
                step={1}
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Advance Notice</Label>
                <span className="text-sm font-medium">{(settings?.advance_notice_min ?? 120) / 60} hours</span>
              </div>
              <Slider
                value={[settings?.advance_notice_min ?? 120]}
                onValueChange={([value]) => 
                  settings && setSettings({ ...settings, advance_notice_min: value })
                }
                min={30}
                max={480}
                step={30}
              />
              <p className="text-xs text-muted-foreground">
                Minimum time before appointment that bookings are accepted
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Appointment Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Appointment Types
            </CardTitle>
            <CardDescription>
              Which appointment types are available online
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="waiter">Waiter Appointments</Label>
                <p className="text-xs text-muted-foreground">Customer waits onsite</p>
              </div>
              <Switch
                id="waiter"
                checked={settings?.waiter_allowed ?? true}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, waiter_allowed: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="dropoff">Drop-off Appointments</Label>
                <p className="text-xs text-muted-foreground">Customer leaves vehicle</p>
              </div>
              <Switch
                id="dropoff"
                checked={settings?.dropoff_allowed ?? true}
                onCheckedChange={(checked) => 
                  settings && setSettings({ ...settings, dropoff_allowed: checked })
                }
              />
            </div>
            {settings?.waiter_allowed && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label>Max Waiters Per Day</Label>
                  <span className="text-sm font-medium">
                    {(settings?.max_waiters_per_day ?? 0) === 0 ? 'Unlimited' : settings?.max_waiters_per_day}
                  </span>
                </div>
                <Slider
                  value={[settings?.max_waiters_per_day ?? 6]}
                  onValueChange={([value]) => 
                    settings && setSettings({ ...settings, max_waiters_per_day: value })
                  }
                  min={0}
                  max={20}
                  step={1}
                />
                <p className="text-xs text-muted-foreground">
                  0 = unlimited waiter appointments
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Buffer Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Customer-Facing Buffer</CardTitle>
            <CardDescription>
              Extra time added to estimates shown to customers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Conservative Buffer</Label>
              <span className="text-sm font-medium">{settings?.conservative_buffer_pct ?? DEFAULT_SETTINGS.conservative_buffer_pct}%</span>
            </div>
            <Slider
              value={[settings?.conservative_buffer_pct ?? DEFAULT_SETTINGS.conservative_buffer_pct]}
              onValueChange={([value]) => 
                settings && setSettings({ ...settings, conservative_buffer_pct: value })
              }
              min={0}
              max={50}
              step={5}
            />
            <p className="text-xs text-muted-foreground">
              Adds extra time to customer-facing estimates to under-promise and over-deliver
            </p>
          </CardContent>
        </Card>

        {/* Diagnostic Default Duration */}
        <Card>
          <CardHeader>
            <CardTitle>Diagnostic Duration</CardTitle>
            <CardDescription>
              Default time for diagnostics, callbacks, and confirmation-required bookings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Default Duration</Label>
              <span className="text-sm font-medium">{settings?.diagnostic_default_minutes ?? 60} min</span>
            </div>
            <Slider
              value={[settings?.diagnostic_default_minutes ?? 60]}
              onValueChange={([value]) => 
                settings && setSettings({ ...settings, diagnostic_default_minutes: value })
              }
              min={30}
              max={180}
              step={15}
            />
            <p className="text-xs text-muted-foreground">
              Used for Lane 2 (Diagnostics), Lane 3A (Requested Work), and Lane 3B (Callbacks)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Allowed Jobs (Lane 1 only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Bookable Services (Lane 1 - Maintenance)
          </CardTitle>
          <CardDescription>
            Select which services customers can directly book online
            <Badge variant="outline" className="ml-2">
              {settings?.allowed_job_keys?.length ?? 0} selected
            </Badge>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-6">
              {Object.entries(jobsByCategory).map(([category, jobs]) => (
                <div key={category} className="space-y-2">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    {category.replace(/_/g, ' ')}
                  </h3>
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {jobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleJobKey(job.id)}
                      >
                        <Checkbox
                          id={job.id}
                          checked={settings?.allowed_job_keys?.includes(job.id) ?? false}
                          onCheckedChange={() => toggleJobKey(job.id)}
                        />
                        <Label htmlFor={job.id} className="text-sm cursor-pointer flex-1">
                          {job.label}{retiredCatalogSuffix(job)}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
